"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RSA = exports.RSAUtils = void 0;
class RSAUtils {
    constructor(crypto = typeof window !== 'undefined' ? window.crypto : typeof self !== 'undefined' ? self.crypto : {}) {
        /********* RSA Config **********/
        //TODO bigger modulus
        //TODO PSS for signing
        this.rsaParams = { name: 'RSA-OAEP' };
        // RSA params for 'import' and 'generate' function.
        this.rsaHashedParams = {
            name: 'RSA-OAEP',
            modulusLength: 2048,
            publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
            hash: { name: 'sha-1' },
        };
        this.rsaLocalStoreIdPrefix = 'org.taktik.icure.rsa.';
        this.rsaKeyPairs = {};
        this.crypto = crypto;
    }
    /**
     * It returns CryptoKey promise, which doesn't hold the bytes of the key.
     * If bytes are needed, you must export the generated key.
     * R
     * @returns {Promise} will be {publicKey: CryptoKey, privateKey: CryptoKey}
     */
    generateKeyPair() {
        const extractable = true;
        const keyUsages = ['decrypt', 'encrypt'];
        return new Promise((resolve, reject) => {
            this.crypto.subtle.generateKey(this.rsaHashedParams, extractable, keyUsages).then(resolve, reject);
        });
    }
    exportKeys(keyPair, privKeyFormat, pubKeyFormat) {
        const pubPromise = this.crypto.subtle.exportKey(pubKeyFormat, keyPair.publicKey);
        const privPromise = this.crypto.subtle.exportKey(privKeyFormat, keyPair.privateKey);
        return Promise.all([pubPromise, privPromise]).then(function (results) {
            return {
                publicKey: results[0],
                privateKey: results[1],
            };
        });
    }
    exportKey(cryptoKey, format) {
        return new Promise((resolve, reject) => {
            this.crypto.subtle.exportKey(format, cryptoKey).then(resolve, reject);
        });
    }
    /**
     *
     * @param publicKey (CryptoKey)
     * @param plainData (Uint8Array)
     */
    encrypt(publicKey, plainData) {
        return new Promise((resolve, reject) => {
            this.crypto.subtle.encrypt(this.rsaParams, publicKey, plainData.buffer ? plainData.buffer : plainData).then(resolve, reject); //Node prefers arrayBuffer
        });
    }
    /**
     *
     * @param privateKey (CryptoKey)
     * @param encryptedData (Uint8Array)
     */
    decrypt(privateKey, encryptedData) {
        return new Promise((resolve, reject) => {
            this.crypto.subtle.decrypt(this.rsaParams, privateKey, encryptedData).then(resolve, reject);
        });
    }
    /**
     *
     * @param format 'jwk', 'spki', or 'pkcs8'
     * @param keydata should be the key data based on the format.
     * @param keyUsages Array of usages. For example, ['encrypt'] for public key.
     * @returns {*}
     */
    importKey(format, keydata, keyUsages) {
        const extractable = true;
        return new Promise((resolve, reject) => {
            this.crypto.subtle.importKey(format, keydata, this.rsaHashedParams, extractable, keyUsages).then(resolve, reject);
        });
    }
    /**
     *
     * @param format 'jwk' or 'pkcs8'
     * @param keydata should be the key data based on the format.
     * @returns {*}
     */
    importPrivateKey(format, keydata) {
        const extractable = true;
        return new Promise((resolve, reject) => {
            this.crypto.subtle.importKey(format, keydata, this.rsaHashedParams, extractable, ['decrypt']).then(resolve, reject);
        });
    }
    /**
     *
     * @param privateKeyFormat 'jwk' or 'pkcs8'
     * @param privateKeydata    should be the key data based on the format.
     * @param publicKeyFormat 'jwk' or 'spki'
     * @param publicKeyData should be the key data based on the format.
     * @returns {Promise|*}
     */
    importKeyPair(privateKeyFormat, privateKeydata, publicKeyFormat, publicKeyData) {
        const extractable = true;
        const privPromise = this.crypto.subtle.importKey(privateKeyFormat, privateKeydata, this.rsaHashedParams, extractable, ['decrypt']);
        const pubPromise = this.crypto.subtle.importKey(publicKeyFormat, publicKeyData, this.rsaHashedParams, extractable, ['encrypt']);
        return Promise.all([pubPromise, privPromise]).then(function (results) {
            return {
                publicKey: results[0],
                privateKey: results[1],
            };
        });
    }
    /**
     *
     * @param id
     * @param keyPair should be JWK
     */
    storeKeyPair(id, keyPair) {
        if (typeof Storage === 'undefined') {
            console.log('Your browser does not support HTML5 Browser Local Storage !');
            throw 'Your browser does not support HTML5 Browser Local Storage !';
        }
        //TODO encryption
        localStorage.setItem(this.rsaLocalStoreIdPrefix + id, JSON.stringify(keyPair));
    }
    /**
     * loads the RSA key pair (hcparty) in JWK, not imported
     *
     * @param id  doc id - hcpartyId
     * @returns {Object} it is in JWK - not imported
     */
    loadKeyPairNotImported(id) {
        if (typeof Storage === 'undefined') {
            console.log('Your browser does not support HTML5 Browser Local Storage !');
            throw 'Your browser does not support HTML5 Browser Local Storage !';
        }
        //TODO decryption
        return JSON.parse(localStorage.getItem(this.rsaLocalStoreIdPrefix + id) || '{}');
    }
    /**
     * Loads and imports the RSA key pair (hcparty)
     *
     * @param id  doc id - hcPartyId
     * @returns {Promise} -> {CryptoKey} - imported RSA
     */
    loadKeyPairImported(id) {
        return new Promise((resolve, reject) => {
            try {
                const jwkKey = localStorage.getItem(this.rsaLocalStoreIdPrefix + id);
                if (jwkKey) {
                    const jwkKeyPair = JSON.parse(jwkKey);
                    if (jwkKeyPair.publicKey && jwkKeyPair.privateKey) {
                        this.importKeyPair('jwk', jwkKeyPair.privateKey, 'jwk', jwkKeyPair.publicKey).then(resolve, (err) => {
                            console.log('Error in RSA.importKeyPair: ' + err);
                            reject(err);
                        });
                    }
                    else {
                        const message = 'Error in RSA.importKeyPair: Invalid key';
                        console.log(message);
                        reject(Error(message));
                    }
                }
                else {
                    const message = 'Error in RSA.importKeyPair: Missing key';
                    console.log(message);
                    reject(Error(message));
                }
            }
            catch (err) {
                reject(err);
            }
        });
    }
}
exports.RSAUtils = RSAUtils;
exports.RSA = new RSAUtils();
//# sourceMappingURL=RSA.js.map